import { getErrors } from '../../functions/errors/GetErrors'
import { ACCESS_KEY, USER_ID_KEY, urlAPI } from '../../urls'
import {
    setSearchAreasFound, setSearchNoAreasFound, setSearchSearchFilters, setUnitsSearchCatalog,
    type Unit,
    type UnitsCategory,
    unitsSetCatalogLoading,
    unitsSetSearchCatalogLoading,
    unitsSlice
} from '../slices/unitsSlice'
import { type AppDispatch, type RootState, store } from '../store'

import _ from 'lodash'
import { type IFile } from '../../types'
import qs from 'qs'

import { type User, userFetchingSuccess } from '../slices/userSlice'

import { type ApiResponse } from '../../types/userTypes/TAuth'
import { dataFetchingFinish, dataFetchingStart } from '../slices/modalSlice'
import customLog from '../../functions/log'
import nanToNull from '../../functions/adapters/universal/nanToNull'
import { nanoid } from 'nanoid'
import { uploadStrapiFile } from './fileActions'
import { fetchUser } from './userActions'
import { unitOwningStatuses } from '../slices/setUnitSlice'
import notifyDelay from '../../functions/shared/notify-helpers/notifyDelay'
import {  scanDocuments } from './documentActions'
import { NOTIFY_DELAY, UNIT_SOLD_NOTIFY_DELAY } from '../../constants'

import dayjs from 'dayjs'
import getUserType from '../../functions/shared/user/getUserType'
import Swal from 'sweetalert2'
import { swalError, swalOptions } from '../../functions/swalOptions'
import { type Document } from '../slices/documentSlice'
import compareNames from '../../functions/shared/user/compareNames/compareNames'
import { deleteDocument } from './documentsActions/deleteDocument'
import { removeDataAttributes } from '../../functions/adapters/universal/removeDataAttributes'
import { type NavigateFunction } from 'react-router-dom'
import { processPropertyDeleteTasks } from './propertyActions'
import { getQueryUnits, unitsPopulate } from '../../functions/shared/api/queries/getQueryUnits'
import { createDocument } from './documentsActions/createDocument'
import {updateDocument} from "./documentsActions/updateDocument";
import { uploadSingleFile } from './documentsActions/upload/uploadSingleFile'
import { getQueryDocuments } from '../../functions/shared/api/queries/getQueryDocuments'
import { fetchDocuments } from './documentActions'
import { getChatList } from './chatListActions'
import { fetchUnits } from './unitActions/fetchUnits'
import { normalizeDisabledTimesForApi } from '../../functions/shared/units/disabledTimes'
import { normalizeCreateUnitInfoForStorage } from '../../functions/shared/units/mainUnitSelectors'
import { appendUnitReviewActivity, mergeUnitReview, type OffMarketActivityEvent, type UnitActivityEvent } from '../../functions/shared/units/unitReviewActivity'

/** Title Deed's Year component is numeric. Date values must never be coerced to timestamps. */
const normalizeTitleDeedYear = (value: unknown): number | undefined => {
    const raw = value instanceof Date && !Number.isNaN(value.getTime())
        ? String(value.getFullYear())
        : typeof (value as any)?.format === 'function'
        ? (value as any).format('YYYY')
        : String(value ?? '').trim()

    return /^\d{4}$/.test(raw) ? Number(raw) : undefined
}

export const sendUnitNotification = async (unit: Unit, rule: string, ruleData?: any): Promise<{ isSuccessful: boolean }> => {
    try {
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
        const sentBodyForNotification = {
            method: 'POST',
            collection: 'user-notifies',
            body: {
                mode: 'publish',
                executeAt: notifyDelay(NOTIFY_DELAY),
                RuleID: unit.id,
                Rule: rule, 
                User: (unit.attributes as any)?.User?.data?.id,
                ...(ruleData || {}),
                Type: 'Mobile',
                publishedAt: null
            },
            query: qs.stringify({
                populate: ['User']
            }, {
                encodeValuesOnly: true
            })
        }

        const notificationResponse = await fetch(`${urlAPI}`, {
            method: 'POST',
            body: JSON.stringify(sentBodyForNotification),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${String(jwt)}`,
                'BSO-Hash': '02ns30nsBSO!'
            }
        })
        const notificationResponseJSON = await notificationResponse.json()
        notificationResponseJSON.isSuccessful = notificationResponseJSON?.response?.error == null
        return notificationResponseJSON
    } catch (e) {
        return {
            isSuccessful: false
        }
    }
}

export const singleUnitFetchingSuccess = (responseJSON: Record<string, any>) => (dispatch: AppDispatch, getState: () => RootState): { isSuccessful: boolean } => {
    try {
        const unitObject = responseJSON.response?.data
        if (unitObject?.id == null) {
            return ({
                isSuccessful: false
            })
        }
        const units = _.cloneDeep(getState().units.units)
        const previousUnit = units.find((unit) => unit.id === unitObject.id)
        const mergedUnitObject = previousUnit == null
            ? unitObject
            : {
                ...previousUnit,
                ...unitObject,
                attributes: {
                    ...(previousUnit.attributes ?? {}),
                    ...(unitObject.attributes ?? {})
                }
            }
        const existingUnitIndex = units.findIndex((unit) => unit.id === unitObject.id)
        const updatedUnits = existingUnitIndex === -1
            ? [...units, mergedUnitObject]
            : units.map((unit, index) => index === existingUnitIndex ? mergedUnitObject : unit)
        dispatch(unitsSlice.actions.unitsFetchingSuccess(updatedUnits))

        const currentUser = _.cloneDeep(getState().user.currentUser)

        if (mergedUnitObject?.attributes?.User?.data?.id === currentUser.id) {
            const unitObjectNoDataAttributes = removeDataAttributes(_.cloneDeep(mergedUnitObject))
            currentUser.Units = [
                ...(currentUser as any)?.Units?.filter((unit: Unit) => unit.id !== unitObjectNoDataAttributes.id),
                unitObjectNoDataAttributes
            ].sort((unitA, unitB) => unitA.id > unitB.id ? 1 : unitA.id < unitB.id ? -1 : 0)
            dispatch(userFetchingSuccess(currentUser as User))
        }
        return {
            isSuccessful: true
        }
    } catch (e) {
        console.error(e)
        return {
            isSuccessful: false
        }
    }
}



export const fetchCatalogUnits = (page: number, category: UnitsCategory, secondCall: boolean = false) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    customLog('no area found', getState().units.noAreasFound)
    customLog('pagination loading', getState().pagination.paginationLoading)
    if (getState().units.noAreasFound || (getState().units.catalogLoading && !secondCall)) {
        return
    }
    const areasFound = getState().units.areasFound
    dispatch(unitsSetCatalogLoading(true))
    try {
        const queries = {
            rent: {
                sort: ['createdAt'],
                populate: unitsPopulate,
                filters: {
                    $and: [
                        {
                            $or: [
                                {
                                    User: {
                                        id: {
                                            $eq: +(process.env.REACT_APP_BSO_USER_ID ?? '57')
                                        }
                                    }
                                },
                                {
                                    $and: [
                                        {
                                            User: {
                                                Category: {
                                                    $eq: 'Landlord'
                                                }
                                            }
                                        },
                                        {
                                            Marketing: {
                                                Status: {
                                                    $eq: 'Enabled'
                                                }
                                            }
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            Marketing: {
                                Use: {
                                    $eq: 'For rent'
                                }
                            }
                        }
                    ]
                },
                pagination: {
                    page,
                    pageSize: 5,
                    withCount: true
                }
            },
            sell: {
                sort: ['createdAt'],
                populate: unitsPopulate,
                filters: {
                    $and: [
                        {
                            $or: [
                                {
                                    User: {
                                        id: {
                                            $eq: +(process.env.REACT_APP_BSO_USER_ID ?? '57')
                                        }
                                    }
                                },
                                {
                                    $and: [
                                        {
                                            User: {
                                                Category: {
                                                    $eq: 'Landlord'
                                                }
                                            }
                                        },
                                        {
                                            Marketing: {
                                                Status: {
                                                    $eq: 'Enabled'
                                                }
                                            }
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            Marketing: {
                                Use: {
                                    $eq: 'For sale'
                                }
                            }
                        }
                    ]
                },
                pagination: {
                    page,
                    pageSize: 5,
                    withCount: true
                }
            },
            all: {
                sort: {
                    createdAt: 'desc'
                }, // ['createdAt:desc'],
                populate: unitsPopulate,
                filters: {
                    $or: [
                        {
                            User: {
                                id: {
                                    $eq: +(process.env.REACT_APP_BSO_USER_ID ?? '57')
                                }
                            }
                        },
                        {
                            $and: [
                                {
                                    User: {
                                        Category: {
                                            $eq: 'Landlord'
                                        }
                                    }
                                },
                                {
                                    Marketing: {
                                        Status: {
                                            $eq: 'Enabled'
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                pagination: {
                    page,
                    pageSize: 5,
                    withCount: true
                }
            }
        }

        let query: Record<string, any>

        if (!areasFound) {
            query = dispatch(applyUnitsFilters({ ...queries[category === "rent" || category === "sell" ? category : "all" ] }, {
                Property: {
                    Area: {
                        $in: (getState().user.currentUser as User)?.Settings?.[0]?.Areas ?? []
                    }
                }
            }))
        } else {
            query = dispatch(applyUnitsFilters({ ...queries[category === "rent" || category === "sell" ? category : "all" ] }, {
                Property: {
                    Area: {
                        $not: {
                            $in: (getState().user.currentUser as User)?.Settings?.[0]?.Areas ?? []
                        }
                    }
                }
            }))
            if (getState().units.catalog?.length >= 5) {
                query.pagination.page = /* Math.max( */query.pagination.page - getState().units.areasPageCount/*, 1) */ + 1
            }
        }

        //  console.log({ query })

        const bodyObject = {
            method: 'GET',
            collection: 'units',
            query: qs.stringify(query, {
                encodeValuesOnly: true
            })
        }

        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            },
            body: JSON.stringify(bodyObject)
        })

        const responseJSON = await response.json()
        // console.log(responseJSON, 'responseJSON')
        // dispatch(setPageCount(noAreaCount + areaCount))
        if (responseJSON.success === true) {
            const fetchedUnits = responseJSON.response.data as Unit[]
            const storedUnits = getState().units.catalog ?? []
            if (fetchedUnits?.length === 0) {
                dispatch(unitsSlice.actions.unitsSetPage(getState().units.page - 1))
            }
            // if (page === 1) {
            _.remove(fetchedUnits, (unit) =>
                _.some(storedUnits, (storedUnit) => storedUnit.id === unit.id)
            )
            const units = [...storedUnits, ...fetchedUnits]
            dispatch(unitsSlice.actions.unitsSetCatalog(units))
            if (!getState().units.areasFound) {
                dispatch(unitsSlice.actions.unitsSetAreasPageCount(responseJSON.response.meta.pagination.pageCount))
            }
            // } else {
            //    const units = [...storedUnits, ...fetchedUnits]
            //    dispatch(unitsSlice.actions.unitsSetCatalog(units))
            // dispatch(unitsSlice.actions.unitsSetPageCount(responseJSON.response.meta.pagination.pageCount))
            // }
            if (fetchedUnits.length < 5) {
                if (!getState().units.areasFound) {
                    dispatch(unitsSetCatalogLoading(false))
                    dispatch(unitsSlice.actions.unitsSetAreasFound(true))
                    await dispatch(fetchCatalogUnits(getState().units.page, category, true))
                } else {
                    dispatch(unitsSlice.actions.unitsSetNoAreasFound(true))
                }
            }
            /* if (fetchedUnits.length < 5 && !areasFound) {
                dispatch(unitsSlice.actions.unitsSetAreasFound(true))
                // dispatch(unitsSlice.actions.unitsSetAreasPageCount(responseJSON.response.meta.pagination.pageCount))
                await dispatch(fetchCatalogUnits(page + 1, category, true))
            } else if (fetchedUnits.length < 5 && !getState().units.noAreasFound) {
                dispatch(unitsSlice.actions.unitsSetNoAreasFound(true))
            } */
        } else {
            // dispatch(unitsSlice.actions.unitsFetchingError(getErrors(responseJSON)))
            throw (new Error())
        }
    } catch (err) {
        console.error(err)
    }
    dispatch(unitsSetCatalogLoading(false))
}

export const fetchSearchCatalogUnits = (page: number) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    // console.log('no area found', getState().units.searchNoAreasFound)
    if (getState().units.noAreasFound || getState().units.searchCatalogLoading) {
        return
    }
    const areasFound = getState().units.searchAreasFound
    dispatch(unitsSetSearchCatalogLoading(true))
    try {
        const searchQuery = {
            sort: {
                createdAt: 'desc'
            }, // ['createdAt:desc'],
            populate: unitsPopulate,
            filters: {
                $or: [
                    {
                        User: {
                            id: {
                                $eq: +(process.env.REACT_APP_BSO_USER_ID ?? '57')
                            }
                        }
                    },
                    {
                        $and: [
                            {
                                User: {
                                    Category: {
                                        $eq: 'Landlord'
                                    }
                                }
                            },
                            {
                                Marketing: {
                                    Status: {
                                        $eq: 'Enabled'
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            pagination: {
                page,
                pageSize: 5,
                withCount: true
            }
        }

        let query: Record<string, any>

        if (!areasFound) {
            query = dispatch(applySearchUnitsFilters(searchQuery, {
                Property: {
                    Area: {
                        $in: (getState().user.currentUser as User)?.Settings?.[0]?.Areas ?? []
                    }
                }
            }))
        } else {
            query = dispatch(applySearchUnitsFilters(searchQuery, {
                Property: {
                    Area: {
                        $not: {
                            $in: (getState().user.currentUser as User)?.Settings?.[0]?.Areas ?? []
                        }
                    }
                }
            }))
            if (getState().units.catalog?.length >= 5) {
                query.pagination.page = query.pagination.page - getState().units.searchAreasPageCount
            }
        }

        //  console.log({ query })

        const bodyObject = {
            method: 'GET',
            collection: 'units',
            query: qs.stringify(query, {
                encodeValuesOnly: true
            })
        }

        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            },
            body: JSON.stringify(bodyObject)
        })

        const responseJSON = await response.json()
        // console.log(responseJSON, 'responseJSON')
        // dispatch(setPageCount(noAreaCount + areaCount))
        if (responseJSON.success === true) {
            const fetchedUnits = responseJSON.response.data as Unit[]
            const storedUnits = getState().units.searchCatalog ?? []
            if (page === 1) {
                _.remove(fetchedUnits, (unit) =>
                    _.some(storedUnits, (storedUnit) => storedUnit.id === unit.id)
                )
                const units = [...storedUnits, ...fetchedUnits]
                dispatch(setUnitsSearchCatalog(units))
            } else {
                const units = [...storedUnits, ...fetchedUnits]
                dispatch(setUnitsSearchCatalog(units))
                // dispatch(unitsSlice.actions.unitsSetPageCount(responseJSON.response.meta.pagination.pageCount))
            }
            if (fetchedUnits.length < 5) {
                if (!getState().units.searchAreasFound) {
                    dispatch(unitsSetSearchCatalogLoading(false))
                    dispatch(setSearchAreasFound(true))
                    void dispatch(fetchSearchCatalogUnits(page))
                } else {
                    dispatch(setSearchNoAreasFound(true))
                }
            }
            if (fetchedUnits.length < 5 && !areasFound) {
                dispatch(setSearchAreasFound(true))
                // dispatch(unitsSlice.actions.unitsSetAreasPageCount(responseJSON.response.meta.pagination.pageCount))
                await dispatch(fetchSearchCatalogUnits(page + 1))
            } else if (fetchedUnits.length < 5 && !getState().units.searchNoAreasFound) {
                dispatch(setSearchNoAreasFound(true))
            }
        } else {
            // dispatch(unitsSlice.actions.unitsFetchingError(getErrors(responseJSON)))
            throw (new Error())
        }
    } catch (err) {
        console.error(err)
    }
    dispatch(unitsSetSearchCatalogLoading(false))
}

export const applyUnitsFilters = (query: any, extraFilter: Record<string, any>) => (dispatch: AppDispatch, getState: () => RootState): Record<string, any> => {
    const filters = { ...getState().units.filters }
    const processedFilters: any = {
        $and: []
    }
    for (let key in filters) {
        let filter: any
        if (key[0] !== '$') {
            if (key[0] === '>') {
                filter = {
                    $in: filters[key]
                }
                key = key.slice(1)
            } else if (key[0] === '<') {
                filter = {
                    $contains: filters[key]
                }
                key = key.slice(1)
            } else if (_.isArray(filters[key])) {
                filter = {
                    $between: filters[key]
                }
            } else if (key.slice(0, 5) === 'FROM-') {
                filter = {
                    $gte: !_.includes([null, undefined, ''], filters[key]) ? Number(filters[key]) : undefined
                }
                key = key.slice(5)
            } else if (key.slice(0, 5) === 'UPTO-') {
                filter = {
                    $lte: !_.includes([null, undefined, ''], filters[key]) ? Number(filters[key]) : undefined
                }
                key = key.slice(5)
            } else if (filters[key] === 'More') {
                filter = {
                    $gt: 3
                }
            } else {
                filter = {
                    $eq: filters[key]
                }
            }
            const keySplit = key.split('_')
            _.reverse(keySplit)
            // console.log('keys')
            // console.log(keySplit)
            let processedFilter = _.reduce(keySplit, (accumulator, splitKey) => {
                const accumulatorCopy = { ...accumulator }
                accumulator = {}
                accumulator[splitKey] = accumulatorCopy
                return accumulator
            }, filter)
            //   console.log('filter', processedFilter)
            if (keySplit?.length > 1) {
                _.omit(processedFilters, [key])
            }
            if (_.includes(['Bathrooms', 'Bedrooms'], key) && processedFilter?.[key]?.$eq != null) {
                customLog(processedFilter, 'PROCESSED FILTER')
                processedFilter = {
                    $or: [
                        processedFilter,
                        {
                            [key]: {
                                $notNull: false
                            }
                        }
                    ]
                }
            }
            //   console.log(processedFilters)
            // processedFilters = { ...processedFilters, ...processedFilter }
            processedFilters.$and.push(processedFilter)
        } else {
            processedFilters.$and.push(filters[key])
            // processedFilters = { ...processedFilters, ...filters[key] }
        }
    }
    query.filters = {
        $and: [
            extraFilter,
            getState().units.searchFilters,
            processedFilters,
            query.filters
        ]
    }
    customLog('QUERY', query)
    return (query)
}

export const applySearchUnitsFilters = (query: any, extraFilter: Record<string, any>) => (dispatch: AppDispatch, getState: () => RootState): Record<string, any> => {
    query.filters = {
        $and: [
            extraFilter,
            getState().units.searchSearchFilters,
            query.filters
        ]
    }
    return (query)
}

export const applyUnitModalFilters = (data: Record<string, any>) => (dispatch: AppDispatch, getState: () => RootState) => {
    const savedFilters = _.cloneDeep(getState().units.filters)
    if (data['SORT-by'] === '') {
        data['SORT-by'] = undefined
        savedFilters['SORT-by'] = undefined
    }
    dispatch(setUnitsFilters({ ...savedFilters, ...data }))
    return ({
        textNotification: '',
        isSuccessful: true,
        textNavigate: -1
    })
}

export const applyUnitSearch = () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { searchString } = getState().units
    if (searchString?.length > 0 && getState().units.filters?.['>Property_Area']?.[0] == null) {
        const searchFilters = {
            /* Property: {
                Area: {
                    $contains: searchString.trim()
                }
            } */
            $or: [
                {
                    Property: {
                        $or: [
                            {
                                Area: {
                                    $contains: searchString.trim()
                                }
                            },
                            {
                                Name: {
                                    $contains: searchString.trim()
                                }
                            }
                        ]
                    }
                },
                !isNaN(+(searchString.trim()))
                    ? {
                        Number: {
                            $eq: +(searchString.trim())
                        }
                    }
                    : undefined
            ]
        }
        dispatch(unitsSlice.actions.unitsSetSearchFilters(searchFilters))
    } else {
        dispatch(unitsSlice.actions.unitsSetSearchFilters({}))
    }
}

export const applyUnitSearchSearch = () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { searchSearchString } = getState().units
    if (searchSearchString?.length > 0) {
        const searchFilters = {
            Property: {
                Area: {
                    $contains: searchSearchString.trim()
                }
            }
        }
        dispatch(setSearchSearchFilters(searchFilters))
    } else {
        dispatch(setSearchSearchFilters({}))
    }
}

export const setUnitsFilters = (payload: Record<string, any>) => unitsSlice.actions.unitsSetFilters(payload)

type TitleDeedOwnerPayload = {
    Name: string
    Valid: boolean
}

const getUnitOwnerSource = (owners: any): any[] => Array.isArray(owners) ? owners.filter(Boolean) : []

const getUnitOwnerName = (owner: any): string => String(owner?.ContactInfo?.Name ?? owner?.Name ?? owner?.name ?? '').trim()

const getOwnerNameKey = (name: string): string => name.toLowerCase().replace(/\s+/g, ' ').trim()

const validateTitleDeedOwnerSource = (owners: any): string | undefined => {
    const ownerSource = getUnitOwnerSource(owners)
    if (ownerSource.length === 0) {
        return 'Add at least one owner before submitting the unit.'
    }

    if (ownerSource.some((owner) => getUnitOwnerName(owner) === '')) {
        return 'Owner name is required before submitting the unit.'
    }
}

const buildTitleDeedOwnersPayload = (owners: any, existingOwners: any[] = []): TitleDeedOwnerPayload[] => {
    const existingValidByName = new Map<string, boolean>()

    existingOwners.forEach((owner) => {
        const name = getUnitOwnerName(owner)
        const valid = owner?.Valid ?? owner?.valid
        if (name !== '' && typeof valid === 'boolean') {
            existingValidByName.set(getOwnerNameKey(name), valid)
        }
    })

    return getUnitOwnerSource(owners).map((owner) => {
        const name = getUnitOwnerName(owner)
        const submittedValid = owner?.Valid ?? owner?.valid
        const existingValid = existingValidByName.get(getOwnerNameKey(name))

        return {
            Name: name,
            Valid: typeof submittedValid === 'boolean'
                ? submittedValid
                : typeof existingValid === 'boolean'
                    ? existingValid
                    : true
        }
    })
}

type OwnerDocumentKind = 'passport' | 'emirates_id'

const ownerDocumentMetaKeys: Record<OwnerDocumentKind, string[]> = {
    passport: ['passport_number', 'passport_name', 'passport_date_of_birth', 'passport_gender', 'passport_nationality', 'passport_expiry_date'],
    emirates_id: ['emirates_id_number', 'emirates_id_name', 'emirates_id_date_of_birth', 'emirates_id_gender', 'emirates_id_nationality', 'emirates_id_expiry_date']
}

const hasOwnerDocumentMetadata = (values: any, kind: OwnerDocumentKind): boolean => ownerDocumentMetaKeys[kind]
    .every((key) => String(values?.[key] ?? '').trim() !== '')

const hasOwnerDocumentSource = (values: any, kind: OwnerDocumentKind): boolean => kind === 'passport'
    ? values?.passport_file != null || values?.uploaded_passport != null
    : (values?.emirates_id_front_file != null && (values?.emirates_id_back_file != null || values?.emirates_id_use_single_file === true)) ||
        (values?.uploaded_emirates_id_front != null && (values?.uploaded_emirates_id_back != null || values?.emirates_id_use_single_file === true))

const getOwnerContactDocumentValue = (values: any, contactKey: 'DateOfBirth' | 'Country' | 'Gender'): string | undefined => {
    const manualValue = String(values?.[contactKey] ?? '').trim()
    if (manualValue !== '') return manualValue
    const documentKey = contactKey === 'DateOfBirth' ? 'date_of_birth' : contactKey === 'Country' ? 'nationality' : 'gender'
    const passportValue = String(values?.[`passport_${documentKey}`] ?? '').trim()
    const emiratesValue = String(values?.[`emirates_id_${documentKey}`] ?? '').trim()
    return passportValue !== '' ? passportValue : emiratesValue !== '' ? emiratesValue : undefined
}

const validateOwnerIdentityDocuments = (owners: any[]): string | undefined => {
    const stateOwners = store.getState().setUnit.owners.filter(Boolean)
    const ownersToValidate = stateOwners.length > 0
        ? stateOwners.map((owner) => owner.values)
        : owners.filter(Boolean)
    for (let index = 0; index < ownersToValidate.length; index++) {
        const values: any = ownersToValidate[index] ?? {}
        const emiratesFrontProvided = values?.emirates_id_front_file != null || values?.uploaded_emirates_id_front != null
        const emiratesBackProvided = values?.emirates_id_back_file != null || values?.uploaded_emirates_id_back != null
        if (emiratesFrontProvided && !emiratesBackProvided && values?.emirates_id_use_single_file !== true) {
            return `Owner #${String(index + 1)}: upload Emirates ID back or enable “Use the front file for both sides”.`
        }
        const available = (['passport', 'emirates_id'] as OwnerDocumentKind[])
            .filter((kind) => hasOwnerDocumentSource(values, kind))
        if (available.length === 0) {
            return `Add a completed Passport or Emirates ID document for owner #${String(index + 1)}.`
        }
        const incomplete = available.find((kind) => !hasOwnerDocumentMetadata(values, kind))
        if (incomplete != null) {
            return `Complete all ${incomplete === 'passport' ? 'Passport' : 'Emirates ID'} fields for owner #${String(index + 1)}.`
        }
        if (available.length === 2) {
            const conflicts: Array<[string, string]> = [
                ['DateOfBirth', 'date_of_birth'],
                ['Country', 'nationality'],
                ['Gender', 'gender']
            ]
            for (const [contactKey, documentKey] of conflicts) {
                const passportValue = String(values?.[`passport_${documentKey}`] ?? '').trim()
                const emiratesValue = String(values?.[`emirates_id_${documentKey}`] ?? '').trim()
                if (passportValue !== '' && emiratesValue !== '' && passportValue !== emiratesValue && String(values?.[contactKey] ?? '').trim() === '') {
                    return `Resolve the conflicting ${contactKey} for owner #${String(index + 1)}.`
                }
            }
        }
    }
}

const persistOwnerDocument = async (values: any, kind: OwnerDocumentKind, existingId?: number): Promise<number | undefined> => {
    if (!hasOwnerDocumentSource(values, kind)) return undefined
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const prefix = kind === 'passport' ? 'passport' : 'emirates_id'
    const refID = `REF_DOC<->${prefix}<->${String(values?.[`${prefix}_number`] ?? 'not_number')}<->${nanoid()}`
    const passportLink = values?.passport_file != null
        ? await uploadSingleFile(values.passport_file, refID)
        : values?.uploaded_passport
    const emiratesFront = values?.emirates_id_front_file != null
        ? await uploadSingleFile(values.emirates_id_front_file, `${refID}<->front`)
        : values?.uploaded_emirates_id_front
    const emiratesBack = values?.emirates_id_back_file != null
        ? await uploadSingleFile(values.emirates_id_back_file, `${refID}<->back`)
        : values?.emirates_id_use_single_file === true ? emiratesFront : values?.uploaded_emirates_id_back

    if ((kind === 'passport' && passportLink == null) || (kind === 'emirates_id' && (emiratesFront == null || emiratesBack == null))) {
        throw new Error(`Unable to upload ${kind === 'passport' ? 'Passport' : 'Emirates ID'} file(s).`)
    }

    const body = {
        RefID: refID,
        Category: kind === 'passport' ? 'Passport' : 'Emirates ID',
        ExpiryDate: values[`${prefix}_expiry_date`],
        Statuses: ['In-process'],
        Type: [{
            __component: kind === 'passport' ? 'documents.passport' : 'documents.emirates-id',
            Number: values[`${prefix}_number`],
            Name: values[`${prefix}_name`],
            DateOfBirth: values[`${prefix}_date_of_birth`],
            Gender: values[`${prefix}_gender`],
            Nationality: values[`${prefix}_nationality`]
        }],
        ...(kind === 'passport' ? { Link: passportLink } : { Link: null, LinkFront: emiratesFront, LinkBack: emiratesBack })
    }
    const request = {
        method: existingId != null ? 'PUT' : 'POST',
        collection: 'documents',
        id: existingId,
        query: getQueryDocuments(),
        body
    }
    const response = await fetch(`${urlAPI}`, {
        method: 'POST',
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` }
    })
    const json = await response.json()
    const id = json?.response?.data?.id
    const didSucceed = json?.success === true || json?.response?.error == null
    if (!didSucceed || !Number.isFinite(Number(id))) {
        throw new Error(`Unable to save ${kind === 'passport' ? 'Passport' : 'Emirates ID'} document.`)
    }
    return Number(id)
}

export const getLandlords = async (data: any, isRepresentative: boolean = false): Promise<Array<{ Landlord: number, Type: string, Representatives: number[], PowerOfAttorney: number | undefined }>> => {
    let representativeData: any = null
    if (data.representative?.ContactInfo?.Name != null && !isRepresentative) {
        representativeData = await getLandlords({ Owners: [data.representative] }, true)
    }
    const getDocumentsArray = (documents: any): any[] => {
        if (Array.isArray(documents)) return documents
        if (Array.isArray(documents?.data)) return documents.data
        return []
    }
    const getDocumentKind = (document: any): 'passport' | 'emirates_id' | null => {
        const doc = removeDataAttributes(_.cloneDeep(document ?? {}))
        const category = String(doc?.Category ?? '').toLowerCase().trim()
        if (category === 'passport') return 'passport'
        if (category === 'emirates id') return 'emirates_id'

        const component = String(doc?.Type?.[0]?.__component ?? '').toLowerCase()
        if (component.includes('passport')) return 'passport'
        if (component.includes('emirates-id')) return 'emirates_id'
        return null
    }
    const getDocId = (document: any): number | undefined => {
        const id = document?.id ?? document?.data?.id
        return Number.isFinite(Number(id)) ? Number(id) : undefined
    }

    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const ownerPromises = _.map(data?.Owners?.filter(Boolean) as any[], async (owner, index) => {
        const ownerValues = store.getState().setUnit.owners[index]?.values ?? {}
        const identityName = String(ownerValues.passport_name ?? ownerValues.emirates_id_name ?? '').trim()
        const contactInfo = {
            ...(owner.ContactInfo ?? {}),
            Name: identityName !== '' ? identityName : owner?.ContactInfo?.Name,
            Email: owner?.ContactInfo?.Email === '' ? null : owner?.ContactInfo?.Email,
            Phone: owner?.ContactInfo?.Phone === '' ? null : owner?.ContactInfo?.Phone,
            DateOfBirth: getOwnerContactDocumentValue(ownerValues, 'DateOfBirth') ?? owner?.ContactInfo?.DateOfBirth,
            Country: getOwnerContactDocumentValue(ownerValues, 'Country') ?? owner?.ContactInfo?.Country,
            Gender: getOwnerContactDocumentValue(ownerValues, 'Gender') ?? owner?.ContactInfo?.Gender
        }
        const names = _.split(contactInfo?.Name, ' ')
        const firstName = names[0]
        const familyName = _.join(_.drop(names), ' ')
        const savedPassportId = await persistOwnerDocument(ownerValues, 'passport', ownerValues.passport)
        const savedEmiratesId = await persistOwnerDocument(ownerValues, 'emirates_id', ownerValues.emirates_id)
        /**
         * Find existing landlord
         */

        const query = {
            populate: {
                ContactInfo: '*',
                Documents: { populate: { Type: '*' } }
            },
            filters: {
                ContactInfo: {
                    FirstName: {
                        $eq: firstName
                    },
                    FamilyName: {
                        $eq: familyName
                    }
                }
            }
        }

        const bodyObject = {
            method: 'GET',
            collection: 'landlords',
            query: qs.stringify(query, {
                encodeValuesOnly: true
            })
        }
        const landlordResponse = await fetch(`${urlAPI}`, {
            method: 'POST',
            body: JSON.stringify(bodyObject),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            }
        })
        const landlordResponseJSON = await landlordResponse.json()

        const existingLandlord = landlordResponseJSON.response?.data?.[0]
        const existingDocuments = getDocumentsArray(existingLandlord?.Documents)
        const existingPassportId = getDocId(existingDocuments.find((doc: any) => getDocumentKind(doc) === 'passport'))
        const existingEmiratesId = getDocId(existingDocuments.find((doc: any) => getDocumentKind(doc) === 'emirates_id'))
        const formPassportId = savedPassportId ?? (isRepresentative
            ? store.getState().setUnit.representative.passport
            : ownerValues.passport)
        const formEmiratesId = savedEmiratesId ?? (isRepresentative
            ? store.getState().setUnit.representative.emirates_id
            : ownerValues.emirates_id)
        const resolvedDocuments = [
            formEmiratesId ?? existingEmiratesId,
            formPassportId ?? existingPassportId
        ].filter(Boolean)

        let landlordID: number
        if (landlordResponseJSON.response?.data?.[0]?.id != null) {
            /**
             * Use existing landlord
             */
            const editBodyObject = {
                method: 'PUT',
                collection: 'landlords',
                id: landlordResponseJSON.response?.data?.[0]?.id,
                body: {
                    RefID: [owner?.ContactInfo?.Name, nanoid()].join('-'),
                    ContactInfo: {
                        FirstName: firstName,
                        FamilyName: familyName,
                        ...contactInfo
                    },
                    Documents: resolvedDocuments
                }
            }
            const editLandlordResponse = await fetch(`${urlAPI}`, {
                method: 'POST',
                body: JSON.stringify(editBodyObject),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwt}`
                }
            })
            const newLandlordResponseJSON = await editLandlordResponse.json()
            landlordID = newLandlordResponseJSON?.response?.data?.id
        } else {
            /**
             * Create new landlord
             */
            const newBodyObject = {
                method: 'POST',
                collection: 'landlords',
                body: {
                    RefID: [owner?.ContactInfo?.Name, nanoid()].join('-'),
                    ContactInfo: {
                        FirstName: firstName,
                        FamilyName: familyName,
                        ...contactInfo
                    },
                    Documents: isRepresentative
                        ? [
                            store.getState().setUnit.representative.emirates_id,
                            store.getState().setUnit.representative.passport
                        ].filter(Boolean)
                        : [
                            formEmiratesId,
                            formPassportId
                        ].filter(Boolean)
                }
            }
            const newLandlordResponse = await fetch(`${urlAPI}`, {
                method: 'POST',
                body: JSON.stringify(newBodyObject),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwt}`
                }
            })
            const newLandlordResponseJSON = await newLandlordResponse.json()
            landlordID = newLandlordResponseJSON?.response?.data?.id
        }
        return {
            Landlord: landlordID,
            Representatives: [
                representativeData?.[0]?.Landlord
            ].filter(Boolean),
            PowerOfAttorney: representativeData != null ? store.getState().setUnit.representative.poa : undefined,
            Type: data.Owners?.length > 1 ? 'Owner of part of property' : 'Owner of property',
            IsDecisionMaker: owner.IsDecisionMaker === 'Yes'
        }
    })
    return await Promise.all(ownerPromises)
}

export const createSingleLandlord = (landlord: { FirstName: any, FamilyName: any, DateOfBirth: any, Country: any, Gender: any, Passport: any, DocumentNumber: any, Email?: any, Phone?: any }, landlordID?: number) => async (dispatch: AppDispatch, getState: () => RootState): Promise<number | undefined> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''

    const newBodyObject = {
        method: landlordID == null ? 'POST' : 'PUT',
        collection: 'landlords',
        id: landlordID,
        body: {
            RefID: landlordID == null ? [String(landlord.FirstName) + ' ' + String(landlord.FamilyName), landlord.DocumentNumber, nanoid()].join('-') : undefined,
            ContactInfo: {
                FirstName: landlord.FirstName,
                FamilyName: landlord.FamilyName,
                DateOfBirth: landlord.DateOfBirth,
                Country: landlord.Country,
                Gender: landlord.Gender,
                Email: landlord.Email,
                Phone: landlord.Phone
            },
            Documents: landlord.Passport != null
                ? [
                    {
                        id: landlord.Passport
                    }
                ]
                : undefined
        }
    }
    const newLandlordResponse = await fetch(`${urlAPI}`, {
        method: 'POST',
        body: JSON.stringify(newBodyObject),
        headers: {
            'Content-Type': 'application/json',
            'BSO-Hash': '02ns30nsBSO!',
            Authorization: `Bearer ${jwt}`
        }
    })

    const newLandlordResponseJSON = await newLandlordResponse.json()

    if (newLandlordResponseJSON.response?.data?.id != null) {
        return newLandlordResponseJSON.response?.data?.id
    }
}

export const createLandlordLandlords = (data: any) => async (dispatch: AppDispatch, getState: () => RootState): Promise<{ Landlord: number, Representatives: number[], PowerOfAttorney: number | undefined }> => {
    let landlordID: number | undefined
    let Representatives: number[] = []
    let PowerOfAttorney: number | undefined
    const owners = getState().setUnit.owners.map((owner) => owner.values.Name)
    const currentUser = getState().user.currentUser as User
    const userID = currentUser.id
    const documents = getState().documents.documents
    if (data.owning_status === unitOwningStatuses[0]) { /** user is the owner of property */
        const userIsOwner = owners.some((owner) => compareNames(
            String(owner),
            {
                FirstName: String(currentUser?.ContactInfo?.FirstName),
                FamilyName: String(currentUser?.ContactInfo?.FamilyName)
            }
        ))
        if (!userIsOwner) {
            await swalError('You are not listed in the Title Deed as one of the landlords')
            throw new Error('Can not create a unit because the user is not one of the landlords')
        }
        const passport = documents.find((doc) => (
            doc.attributes.User?.data?.id === userID &&
            doc.attributes?.Type?.[0]?.__component === 'documents.passport' &&
            dayjs().isBefore(dayjs(doc.attributes.ExpiryDate))
        ))
        if (passport != null) { /** User has the passport uploaded */
            // create landlord
            if ((passport.attributes as any).Landlord.data.id != null) {
                landlordID = (passport.attributes as any).Landlord.data.id
            } else {
                landlordID = await dispatch(createSingleLandlord({
                    FirstName: currentUser?.ContactInfo?.FirstName,
                    FamilyName: currentUser?.ContactInfo?.FamilyName,
                    Country: currentUser?.ContactInfo?.Country,
                    DateOfBirth: currentUser?.ContactInfo?.DateOfBirth,
                    Gender: currentUser?.ContactInfo?.Gender,
                    Passport: passport.id,
                    DocumentNumber: passport.attributes?.Type?.[0]?.Number as string
                }))
            }
        } else { /** User doesn't have passport uploaded */
            let passport: Document | undefined
            await Swal.fire({
                ...(swalOptions('warning')),
                title: 'Upload passport',
                text: 'Please upload your passport so we can add your unit',
                input: 'file',
                inputLabel: 'Upload passport',
                inputAttributes: {
                    'aria-label': 'Upload your passport'
                },
                showLoaderOnConfirm: true,
                preConfirm: async () => {
                    const confirmButton = Swal.getConfirmButton()
                    if (confirmButton != null) {
                        confirmButton.style.display = 'none'
                    }

                    const fileList = Swal.getInput()?.files
                    if (fileList != null && fileList?.length > 0) {
                        // writes created document into modal.documents[0]
                        await dispatch(scanDocuments(fileList, 'Passport', false, undefined, 'user', true))
                        const passportID = getState().modal.documents[0]
                        passport = getState().documents.documents.find((doc) => doc.id === passportID)
                        if (passport != null) {
                            return true
                        }
                    }

                    if (confirmButton != null) {
                        confirmButton.style.display = 'block'
                    }
                    Swal.showValidationMessage('Upload a valid passport')
                    return false
                }
            })
            landlordID = await dispatch(createSingleLandlord({
                FirstName: currentUser?.ContactInfo?.FirstName,
                FamilyName: currentUser?.ContactInfo?.FamilyName,
                Country: currentUser?.ContactInfo?.Country,
                DateOfBirth: currentUser?.ContactInfo?.DateOfBirth,
                Gender: currentUser?.ContactInfo?.Gender,
                Passport: (passport as Document).id,
                DocumentNumber: (passport as Document).attributes?.Type?.[0]?.Number as string
            }))
        }
    } else { /** user is not the owner of the property */
        let passport: Document | undefined
        await Swal.fire({
            ...(swalOptions('warning')),
            title: 'Upload the owner\'s passport',
            text: 'Please upload the owner\'s passport so we can add your unit',
            input: 'file',
            inputLabel: 'Upload the owner\'s passport',
            inputAttributes: {
                'aria-label': 'Upload the owner\'s passport'
            },
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                const confirmButton = Swal.getConfirmButton()
                if (confirmButton != null) {
                    confirmButton.style.display = 'none'
                }

                const fileList = Swal.getInput()?.files
                if (fileList != null && fileList?.length > 0) {
                    // writes created document into modal.documents[0]
                    await dispatch(scanDocuments(fileList, 'Passport', true, `RefDOC-Passport-${nanoid()}`, 'users'))
                    const passportID = getState().modal.documents[0]
                    passport = getState().documents.documents.find((doc) => doc.id === passportID)
                    const landlordIsOwner = owners.some((owner) => compareNames(
                        String(owner),
                        String(passport?.attributes?.Type?.[0]?.Name)
                    ))

                    if (!landlordIsOwner) {
                        Swal.showValidationMessage('The passport holder is not listed in title deed as owner')
                        return false
                    }

                    if (passport != null) {
                        return true
                    }
                }

                if (confirmButton != null) {
                    confirmButton.style.display = 'block'
                }
                Swal.showValidationMessage('Upload a valid passport')
                return false
            }
        })
        let POA: Document | undefined
        await Swal.fire({
            ...(swalOptions('warning')),
            title: 'Upload the POA',
            text: 'Please upload the power of attorney document',
            input: 'file',
            inputLabel: 'Upload the POA',
            inputAttributes: {
                'aria-label': 'Upload the owner\'s passport'
            },
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                const confirmButton = Swal.getConfirmButton()
                if (confirmButton != null) {
                    confirmButton.style.display = 'none'
                }

                const fileList = Swal.getInput()?.files
                if (fileList != null && fileList?.length > 0) {
                    const poaResponse = await dispatch(createDocument(fileList, {
                        RefID: `Power of Attorney-${dayjs().toISOString()}`,
                        Type: [
                            {
                                __component: 'documents.poa'
                            }
                        ]
                    }, 'property'))
                    POA = poaResponse.response.data as Document
                    return true
                }

                if (confirmButton != null) {
                    confirmButton.style.display = 'block'
                }
                Swal.showValidationMessage('Upload a valid power of attorney')
                return false
            }
        })

        PowerOfAttorney = POA?.id

        if (PowerOfAttorney == null) {
            await swalError('Some error occurred while uploading POA. Please, try again later')
            throw new Error('Can not create landlord (createLandlordLandlords function) -> no Power of attorney')
        }

        const userDocument = documents.find((doc) => (
            doc.attributes.User?.data?.id === userID &&
            ['documents.passport', 'documents.emirates-id'].includes(String(doc.attributes?.Type?.[0]?.__component)) &&
            dayjs().isBefore(dayjs(doc.attributes.ExpiryDate))
        ))

        Representatives = [
            await dispatch(createSingleLandlord({
                FirstName: currentUser?.ContactInfo?.FirstName,
                FamilyName: currentUser?.ContactInfo?.FamilyName,
                Country: currentUser?.ContactInfo?.Country,
                DateOfBirth: currentUser?.ContactInfo?.DateOfBirth,
                Gender: currentUser?.ContactInfo?.Gender,
                Passport: userDocument?.id,
                DocumentNumber: (userDocument as Document)?.attributes?.Type?.[0]?.Number as string
            })) ?? -1
        ]

        if (Representatives[0] === -1) {
            await swalError('Some error occurred while adding landlord representatives info to unit. Please, try again later')
            throw new Error('Can not create landlord (createLandlordLandlords function) -> no Representatives')
        }

        landlordID = await dispatch(createSingleLandlord({
            FirstName: passport?.attributes?.Type?.[0]?.Name?.split(' ')?.[0],
            FamilyName: passport?.attributes?.Type?.[0]?.Name?.split(' ')?.slice(1).join(' '),
            Country: undefined,
            DateOfBirth: undefined,
            Gender: undefined,
            Passport: (passport as Document)?.id,
            DocumentNumber: (passport as Document)?.attributes?.Type?.[0]?.Number as string
        }))
    }
    if (landlordID == null) {
        await swalError('Some error occurred while adding landlord info to unit. Please, try again later')
        throw new Error('Can not create landlord (createLandlordLandlords function) -> no landlordID')
    }
    return {
        Landlord: landlordID,
        Representatives,
        PowerOfAttorney
    }
}

export const addUnit = (data: any) => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {

    try {
        if (getState().modal.drawerOpen != null) {
            return {
                ignoreSubmit: true
            }
        }
        dispatch(dataFetchingStart())
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''

        // const uploadPromise = Promise.all(_.map(data.Images, async (file) => await uploadStrapiFile(file, 'properties')))
        const properties = getState().property.properties
        const property = properties?.find(property => property.id === data.Property)
        // const images: Array<IFile | null> = getState().modal.images
        // const storedImages = _.filter(images, (image) => !_.includes(image?.data, 'data:'))
        // const notUploadedImages = _.filter(images, (image) => _.includes(image?.data, 'data:'))

        // const uploadedImages = await Promise.all(_.map(notUploadedImages, async (image) => {
        //     const res: Response = await fetch(image?.data ?? '')
        //     const blob: Blob = await res.blob()
        //     const file = new File([blob], `unit-image-${nanoid()}`, { type: blob.type })
        //     return await uploadStrapiFile(file, 'properties', null, `unit-image-${nanoid()}`)
        // }))

        data.title_deed = undefined
        if (Object.prototype.hasOwnProperty.call(data, 'ERPUnitID')) {
            data.ERPUnitID = data.ERPUnitID === '' || data.ERPUnitID == null
                ? null
                : data.ERPUnitID
        }

        // data.RefID = `${String(data.Number)}-${String(property?.attributes?.Name)}-${String(property?.attributes?.Area)}-${nanoid()}`
        // TODO handle null property properly
        data.RefID = property != null
            ? `${String(data.Number)}-${String(property?.attributes?.Name)}-${String(property?.attributes?.Area ?? nanoid())}`
            : `${String(data.Number)}-${nanoid()}`

        // data.Images = _.map([...storedImages, ...uploadedImages], (image) => image?.data)

        data.User = getState().user.currentUser.id
        data.Documents = getState().modal.documents?.map(document => ({ id: document })) ?? []
        // if (data.Bedrooms !== 'Studio') {
        //     data.Bedrooms = nanToNull(data.Bedrooms)
        // } else {
        //     data.Bedrooms = 0
        // }

        data.Bedrooms = nanToNull(data.Bedrooms)
        data.ParkingSlotsAmount = normalizeNumberField(data.ParkingSlotsAmount);
        data.Category  =   data.UnitCategory
        data.Bathrooms = nanToNull(data.Bathrooms)
        data.SizeArea = nanToNull(data.SizeArea)
        data.ACCount = nanToNull(data.ACCount) ?? 0
        data.AdvertisementSpaces = data.AdvertisementSpaces === 'Open' ? null : nanToNull(data.AdvertisementSpaces)
        data.ManagedBy = 'BSO'
        data.Statuses = data.Statuses?.split('-') ?? []
        data.GuestBathroom = data.GuestBathroom === 'Yes'
        data.MaidsRoom = data.MaidsRoom === 'Yes'
        const titleDeedOwnerSourceError = validateTitleDeedOwnerSource(data.Owners)
        if (titleDeedOwnerSourceError != null) {
            dispatch(dataFetchingFinish())
            return {
                isSuccessful: false,
                textNotification: titleDeedOwnerSourceError
            }
        }

        const ownerIdentityError = getUserType({ checkForAdmin: true }) === 'Admin'
            ? validateOwnerIdentityDocuments(data.Owners)
            : undefined
        if (ownerIdentityError != null) {
            dispatch(dataFetchingFinish())
            return { isSuccessful: false, textNotification: ownerIdentityError }
        }

        data.Landlords = await getLandlords(data)
        data.OccupiedUntil = data?.Statuses?.includes('Occupied') === true ? dayjs(data.OccupiedUntil).add(4, 'hours') : null
        data.DisabledTimes = normalizeDisabledTimesForApi(
            getState().setUnit.staticForm?.find((rowItem) => rowItem.id === 'DisabledTimes')?.value ?? []
        )
        data = normalizeCreateUnitInfoForStorage(data, data.Type, data.UnitCategory ?? data.Category)
        data.SpecialRequest = {}
        data?.tenant_requirements?.forEach((requirement: string) => {
            data.SpecialRequest[requirement] = true
        })
        if (getState().modal?.scannedData?.failedToScan?.url != null) {
            const response = await dispatch(createDocument(null, {
                RefID: `REF_DOC_title_deed_${new Date().getTime()}`,
                ExpiryDate: data.Scanned.DocumentExpiryDate || undefined,
                Type: [
                    {
                        __component: 'documents.title-deed',
                        Number: data.Scanned.DocumentNumber,
                        Year: normalizeTitleDeedYear(data.Scanned.DocumentYear),
                        Type: data.Scanned.DocumentType ?? data.titleDeedPropertyType, // data.Scanned.DocumentType
                        Owners: buildTitleDeedOwnersPayload(data.Owners)
                    }
                ]
            }, undefined, undefined, getState()?.modal?.scannedData?.failedToScan?.url))
            if (response.isSuccessful) {
                data.Documents = [...data.Documents, { id: response?.response?.data?.id }]
            }
        }

        // TODO updateDocument
        const unitDocsIDs = data.Documents.map((doc: any) => doc.id)
        const unitTitleDeedDocs = getState().documents.documents.filter((doc) => unitDocsIDs.includes(doc.id) === true && doc?.attributes?.Type?.[0]?.__component === 'documents.title-deed')
        const titleDeedDoc = unitTitleDeedDocs.length > 0 ? unitTitleDeedDocs[unitTitleDeedDocs.length - 1] : undefined
      
        if (titleDeedDoc != null) {
            const existingTitleDeedOwners = (titleDeedDoc?.attributes?.Type?.[0] as any)?.Owners
            await dispatch(updateDocument({
                id: titleDeedDoc.id,
                data: {
                    ...(data.Scanned.DocumentExpiryDate != null && data.Scanned.DocumentExpiryDate !== '' ? { ExpiryDate: data.Scanned.DocumentExpiryDate } : {}),
                    Type: [{
                        Type: data.titleDeedPropertyType,
                        ...(normalizeTitleDeedYear(data.Scanned.DocumentYear) != null ? { Year: normalizeTitleDeedYear(data.Scanned.DocumentYear) } : {}),
                        Owners: buildTitleDeedOwnersPayload(data.Owners, existingTitleDeedOwners)
                    }]
                }
            }))
        }

        // if (data.special_requests === 'Yes') {
        //     data.Statuses = Array.from(
        //         new Set(
        //             [...data.Statuses, 'Special Request']
        //         )
        //     )
        // } else {
        //     data.Statuses = data.Statuses.filter((status: string) => status !== 'Special Request')
        // }

        data = _.omit(data, ['Owners', 'Scanned'])

        if (getUserType({ checkForAdmin: true }) === 'Landlord') {
            // TODO create landlords
            data.Landlords = [
                await dispatch(createLandlordLandlords(data))
            ]
        }

        const bodyObject = {
            method: 'POST',
            collection: 'units',
            body: data,
            query: getQueryUnits()
        }

        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            body: JSON.stringify(bodyObject),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            }
        })

        const responseJSON = await response?.json() as unknown as any
        responseJSON.isSuccessful = responseJSON?.response?.error == null
        responseJSON.textNotification = 'Unit is added successfully'
        responseJSON.textNavigate = -1
        dispatch(dataFetchingFinish())
        const { isSuccessful } = dispatch(singleUnitFetchingSuccess(responseJSON))
        if (responseJSON.isSuccessful === true) {
            void dispatch(fetchDocuments(true))
        }
        if (!isSuccessful) {
            void dispatch(fetchUser(true))
            void dispatch(fetchUnits(true))
        }
        void dispatch(processPropertyDeleteTasks(Number(data.Property)))
        return responseJSON
    } catch (error) {
        dispatch(dataFetchingFinish())
        console.log('SUBMIT ERROR', error)
        return {
            isSuccessful: false,
            textNotification: error instanceof Error ? error.message : 'Unable to save unit owner documents.'
        }
    }
}

export const updateUnit = (data: any, unitID: number, isMarketing: boolean = false, omitUser: boolean = false) => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    try {
        console.log(data,"@data")
        // console.log('IGNORE CONDITIONS', getState().modal.drawerOpen != null, !isMarketing)
        if (getState().modal.drawerOpen != null && (getState().modal.drawerOpen !== 'edit_unit_marketing_modal' || !getState().setUnit.changeDetected) && !isMarketing) {
            return {
                ignoreSubmit: true
            }
        }
        dispatch(dataFetchingStart())
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
        if (!isMarketing && Object.prototype.hasOwnProperty.call(data, 'ERPUnitID')) {
            data.ERPUnitID = data.ERPUnitID === '' || data.ERPUnitID == null
                ? null
                : data.ERPUnitID
        }
        if (!isMarketing && Object.prototype.hasOwnProperty.call(data, 'CataloguePriority')) {
            const priority = Number(data.CataloguePriority)
            data.CataloguePriority = [1, 2, 3].includes(priority) ? priority : null
        }
        const unit = _.cloneDeep(getState().units.units?.find((unit) => unit.id === unitID))
        const images: Array<IFile | null> = getState().modal.images
        const storedImages = _.filter(images, (image) => !_.includes(image?.data, 'data:'))
        const notUploadedImages = _.filter(images, (image) => _.includes(image?.data, 'data:'))

        const uploadedImages = await Promise.all(_.map(notUploadedImages, async (image) => {
            const res: Response = await fetch(image?.data ?? '')
            const blob: Blob = await res.blob()
            const file = new File([blob], `unit-image-${nanoid()}`, { type: blob.type })
            return await uploadStrapiFile(file, 'units', String(unitID)/*, `unit-image-${nanoid()}` */)
        }))
        console.log(uploadedImages,"<<< uploadedImages <<<")

        const additionalDocuments = data.AdditionalDocuments ?? []
        const storedDocuments = additionalDocuments.filter(_.isString) ?? []
        const uploadedDocuments = await Promise.all(_.map(additionalDocuments.filter((file: any) => !_.isString(file)), async (file) => {
            return await uploadStrapiFile(file, 'units', String(unitID)/*, `unit-additional-doc-${nanoid()}` */)
        }))
        if (!omitUser) {
            data.User = getState().user.currentUser.id
        }

        if (isMarketing) {
            data.Marketing.DiscountPrice = nanToNull(data?.Marketing?.DiscountPrice)
            // The first image will stay the first
            if (storedImages.includes(images[0])) {
                data.Images = _.map([...storedImages, ...uploadedImages], (image) => image?.data)
            } else {
                data.Images = _.map([...uploadedImages, ...storedImages], (image) => image?.data)
            }
            data.AdditionalDocuments = [
                ...storedDocuments,
                ...(uploadedDocuments.map(doc => doc?.data).filter(Boolean) ?? [])
            ]
            const hasMortgage = data.Statuses === 'Available'
            if (hasMortgage && unit?.attributes?.Statuses?.includes('Mortgage') === false) {
                data.Statuses = ['Mortgage', ...(unit?.attributes?.Statuses ?? [])]
            } else if (!hasMortgage && unit?.attributes?.Statuses?.includes('Mortgage') === true) {
                data.Statuses = unit?.attributes?.Statuses?.filter(status => status !== 'Mortgage')
            } else {
                data.Statuses = unit?.attributes?.Statuses ?? undefined
            }
            const unitSold = unit?.attributes?.Statuses?.includes('Sold') === true
            const formSold = data['sold-rented'] === 'Rented/Sold'
            const unitDrafted = unit?.attributes?.Statuses?.includes('Drafted') === true
            const formDrafted = data['sold-rented'] === 'Drafted'
            if (!unitSold && formSold) {
                data.Statuses = ['Sold', ...(data.Statuses ?? [])]
                data.Statuses = data.Statuses.filter((status: string) => status !== 'Drafted')
                data.HideDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24)
            } else if (unitSold && !formSold) {
                data.Statuses = data.Statuses.filter((status: string) => status !== 'Sold')
                data.HideDate = null
            }
            if (!unitDrafted && formDrafted) {
                data.Statuses = ['Drafted', ...(data.Statuses ?? [])]
                data.Statuses = data.Statuses.filter((status: string) => status !== 'Sold')
                data.HideDate = null
            } else if (unitDrafted && !formDrafted) {
                data.Statuses = data.Statuses.filter((status: string) => status !== 'Drafted')
                data.HideDate = null
            }
            data = _.omit(data, 'sold-rented')

            if (data.Video?.[0] != null && typeof data.Video !== 'string') {
                const video = await uploadStrapiFile(data.Video[0], 'properties', String(unitID), `unit-video-${nanoid()}`)
                data.Video = video?.data
            } else {
                data.Video = undefined
            }
            if (data.FloorPlan?.[0] != null && typeof data.FloorPlan !== 'string') {
                const floorPlan = await uploadStrapiFile(data.FloorPlan[0], 'properties', String(unitID), `unit-floor-${nanoid()}`)
                data.FloorPlan = floorPlan?.data
            } else {
                data.FloorPlan = undefined
            }
            if (data.Marketing.MarketingFormTemplate?.[0] != null) {
                const marketingFormTemplate = await uploadStrapiFile(
                    data.Marketing.MarketingFormTemplate?.[0],
                    'units',
                    String(unitID),
                    `unit-marketing-form-template-${nanoid()}`
                )
                data.Marketing.MarketingFormTemplate = marketingFormTemplate?.data ?? (unit?.attributes?.Marketing as any)?.MarketingFormTemplate
            } else {
                data.Marketing.MarketingFormTemplate = (unit?.attributes?.Marketing as any)?.MarketingFormTemplate
            }
            data.LegalNoticeServed = data.LegalNoticeServed === 'Yes'
 
            if (_.isString(data?.CurrentRent) && data?.CurrentRent?.trim() === '') {
                data.CurrentRent = null
            } else {
                data.CurrentRent = nanToNull(Number(data.CurrentRent))
            }
            if (_.isString(data?.AnnualServiceCharge) && data?.AnnualServiceCharge?.trim() === '') {
                data.AnnualServiceCharge = null
            } else {
                data.AnnualServiceCharge = nanToNull(Number(data.AnnualServiceCharge)) ?? null
            }
        } else {
            data.title_deed = undefined

            if (data.Bedrooms !== 'Studio') {
                data.Bedrooms = nanToNull(data.Bedrooms)
            } else {
                data.Bedrooms = 0
            }
            data.Bathrooms = nanToNull(data.Bathrooms)
            data.SizeArea = nanToNull(data.SizeArea)
            if (data.AdvertisePrice != null) {
                data.AdvertisePrice = nanToNull(data.AdvertisePrice) ?? 0
            }
            if (data.ACCount != null) {
                data.ACCount = nanToNull(data?.ACCount) ?? 0
            }
            data.AdvertisementSpaces = data.AdvertisementSpaces === 'Open' ? null : nanToNull(data.AdvertisementSpaces)
          data.ParkingSlotsAmount = normalizeNumberField(data.ParkingSlotsAmount); 
            data.Category  =   data.UnitCategory
            data.GuestBathroom = data.GuestBathroom === 'Yes'
            data.MaidsRoom = data.MaidsRoom === 'Yes'
            data.OccupiedUntil = data?.Statuses?.includes('Occupied') === true ? dayjs(data.OccupiedUntil).add(4, 'hours') : null
            data.DisabledTimes = normalizeDisabledTimesForApi(
                getState().setUnit.staticForm.find((rowItem) => rowItem.id === 'DisabledTimes')?.value ?? []
            )
            data = normalizeCreateUnitInfoForStorage(
                data,
                data.Type ?? unit?.attributes?.Type,
                data.UnitCategory ?? data.Category ?? (unit?.attributes as any)?.Category
            )

            data.SpecialRequest = {}
            data?.tenant_requirements?.forEach((requirement: string) => {
                data.SpecialRequest[requirement] = true
            })

            const hasMortgage = unit?.attributes?.Statuses?.includes('Mortgage') === true
            const unitSold = unit?.attributes?.Statuses?.includes('Sold') === true
            if (data.Statuses != null) {
                data.Statuses = hasMortgage
                    ? ['Mortgage', ...data.Statuses?.split('-')]
                    : data?.Statuses?.split('-')
                if (unitSold) {
                    data.Statuses = ['Sold', ...data.Statuses]
                }
            }
            if (data.special_requests === 'Yes') {
                data.Statuses = Array.from(
                    new Set(
                        [...data.Statuses, 'Special Request']
                    )
                )
            } else {
                data.Statuses = data.Statuses.filter((status: string) => status !== 'Special Request')
            }
            // if (data.special_requests === 'Yes') {
            //     data.Statuses = Array.from(
            //         new Set(
            //             [...data.Statuses, 'Special Request']
            //         )
            //     )
            // } else {
            //     data.Statuses = data.Statuses.filter((status: string) => status !== 'Special Request')
            // }

            data.Documents = getState().modal.documents?.map(document => ({ id: document })) ?? []

            const titleDeedOwnerSourceError = validateTitleDeedOwnerSource(data.Owners)
            if (titleDeedOwnerSourceError != null) {
                dispatch(dataFetchingFinish())
                return {
                    isSuccessful: false,
                    textNotification: titleDeedOwnerSourceError
                }
            }

            const ownerIdentityError = getUserType({ checkForAdmin: true }) === 'Admin'
                ? validateOwnerIdentityDocuments(data.Owners)
                : undefined
            if (ownerIdentityError != null) {
                dispatch(dataFetchingFinish())
                return { isSuccessful: false, textNotification: ownerIdentityError }
            }

            data.Landlords = await getLandlords(data)

            if (getState().modal?.scannedData?.failedToScan?.url != null) {
                const response = await dispatch(createDocument(null, {
                    RefID: `REF_DOC_title_deed_${new Date().getTime()}`,
                    ExpiryDate: data?.Scanned?.DocumentExpiryDate || undefined,
                    Type: [
                        {
                            __component: 'documents.title-deed',
                            Number: data?.Scanned?.DocumentNumber,
                            Year: normalizeTitleDeedYear(data?.Scanned?.DocumentYear),
                            Type: data.Scanned.DocumentType ?? data?.titleDeedPropertyType, // data?.Scanned?.DocumentType
                            Owners: buildTitleDeedOwnersPayload(data.Owners)
                        }
                    ]
                }, undefined, undefined, getState().modal.scannedData.failedToScan.url))
                if (response.isSuccessful) {
                    data.Documents = [...data.Documents, { id: response?.response?.data?.id }]
                }
            }

            const unitDocsIDs = data.Documents.map((doc: any) => doc.id)
            const unitTitleDeedDocs = getState().documents.documents.filter((doc) => unitDocsIDs.includes(doc.id) === true && doc?.attributes?.Type?.[0]?.__component === 'documents.title-deed')
            const titleDeedDoc = unitTitleDeedDocs.length > 0 ? unitTitleDeedDocs[unitTitleDeedDocs.length - 1] : undefined
         
            if (titleDeedDoc != null) {
                const existingTitleDeedOwners = (titleDeedDoc?.attributes?.Type?.[0] as any)?.Owners
                await dispatch(updateDocument({
                    id: titleDeedDoc.id,
                    data: {
                        ...(data?.Scanned?.DocumentExpiryDate != null && data.Scanned.DocumentExpiryDate !== '' ? { ExpiryDate: data.Scanned.DocumentExpiryDate } : {}),
                        Type: [{
                            Type: data.titleDeedPropertyType,
                            ...(normalizeTitleDeedYear(data?.Scanned?.DocumentYear) != null ? { Year: normalizeTitleDeedYear(data?.Scanned?.DocumentYear) } : {}),
                            Owners: buildTitleDeedOwnersPayload(data.Owners, existingTitleDeedOwners)
                        }]
                    }
                }))
            }

            data = _.omit(data, ['Owners', 'Scanned'])
        }

        if (isMarketing && unit?.attributes?.Marketing == null) {
            data.CreateDate = new Date()
        }

        const bodyObject = {
            method: 'PUT',
            collection: 'units',
            id: unitID,
            body: data,
            query: getQueryUnits()
            // query: qs.stringify({
            //     populate: unitsPopulate
            // }, {
            //     encodeValuesOnly: true
            // })
        }
        console.log(bodyObject,"bodyObject")

        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            body: JSON.stringify(bodyObject),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            }
        })
        const responseJSON = await response.json() as unknown as any
        console.log(responseJSON,"responseJSON >>>> responseJSON")
        responseJSON.isSuccessful = responseJSON?.response?.error == null
        if (!isMarketing && responseJSON.isSuccessful === true) {
            responseJSON.textNavigate = '/landlord/my_units'
            responseJSON.textNotification = 'Unit updated successfully'
        }
        if (isMarketing && responseJSON.isSuccessful === true) {
            if (unit?.attributes?.Marketing == null && data?.Statuses?.includes('Drafted') === true && !omitUser) {
                void sendUnitNotification(responseJSON.response.data as Unit, 'user-unit-new')
            }
        }
        const { isSuccessful } = dispatch(singleUnitFetchingSuccess(responseJSON))
        if (responseJSON.isSuccessful === true) {
            void dispatch(fetchDocuments(true))
        }
        if (!isSuccessful) {
            void dispatch(fetchUser(true))
            void dispatch(fetchUnits(true))
        }
        if (!isMarketing) {
            void dispatch(processPropertyDeleteTasks(Number(data.Property)))
        }
        dispatch(dataFetchingFinish())
        return responseJSON
    } catch (e) {
        console.error('Error while updating unit: ', e)
        dispatch(dataFetchingFinish())
        return {
            isSuccessful: false
        }
    }
}

export const updateUnitReview = (unitID: number, unitReview: any) => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    try {
        dispatch(dataFetchingStart())
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
        const currentUnitReview = getState().units.units.find((unit) => unit.id === unitID)?.attributes?.UnitReview
        // Unit Review is a shared JSON document. A review edit must never erase activity/unknown keys.
        const mergedUnitReview = mergeUnitReview(currentUnitReview, unitReview)
        const bodyObject = {
            method: 'PUT',
            collection: 'units',
            id: unitID,
            body: {
                UnitReview: mergedUnitReview
            },
            query: getQueryUnits()
        }

        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            body: JSON.stringify(bodyObject),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            }
        })
        const responseJSON = await response.json() as unknown as any
        responseJSON.isSuccessful = responseJSON?.response?.error == null
        if (responseJSON.isSuccessful === true) {
            responseJSON.textNotification = 'Unit review updated successfully'
        }

        const responseUnitReview = responseJSON?.response?.data?.attributes?.UnitReview ?? null
        const responseHasFreshUnitReview = _.isEqual(responseUnitReview, mergedUnitReview)
        const { isSuccessful } = dispatch(singleUnitFetchingSuccess(responseJSON))
        if (!isSuccessful || !responseHasFreshUnitReview) {
            await dispatch(fetchUnits(true))
        }
        dispatch(dataFetchingFinish())
        return responseJSON
    } catch (e) {
        console.error('Error while updating unit review: ', e)
        dispatch(dataFetchingFinish())
        return {
            isSuccessful: false,
            textNotification: e instanceof Error ? e.message : 'Unable to update unit owner documents.'
        }
    }
}

export const appendUnitActivity = (unitID: number, event: UnitActivityEvent) => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    const currentReview = getState().units.units.find((unit) => unit.id === unitID)?.attributes?.UnitReview
    return await dispatch(updateUnitReview(unitID, appendUnitReviewActivity(currentReview, event)))
}

export const archiveUnit = (unitID: number, navigate?: NavigateFunction) => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const unit = getState().units.units.find(unitItem => unitItem.id === unitID)
    const Statuses = (unit?.attributes?.Statuses ?? []).filter(status => status !== 'Archived')
    const unitIsAvailable = unit?.attributes?.Statuses?.includes('Archived') !== true
    if (unitIsAvailable) {
        Statuses.push('Archived')
    }
    
    const promptResponse = await Swal.fire({
        ...swalOptions('info'),
        title: !unitIsAvailable ? 'This unit will be unarchived' : 'This unit will be archived',
        text: !unitIsAvailable ? 'It will be available in the catalog. Are you sure you want to continue?' : 'It will be removed from the catalog. Are you sure you want to continue?',
        showCancelButton: true,
        cancelButtonText: 'Go back',
        showConfirmButton: true,
        confirmButtonText: !unitIsAvailable ? 'Unarchive unit' : 'Archive unit'
    })
    if (promptResponse.isDismissed) {
        if (navigate != null) {
            navigate(1)
        }

        return {
            isSuccessful: false,
            textNavigate: -1
        }
    }

    dispatch(dataFetchingStart())
    const currentUnit = store.getState().units.units.filter((u) => u.id === unitID)

    const bodyObject = {
        method: 'PUT',
        collection: 'units',
        id: unitID,
        body: {
            Statuses,
            CreateDate: !unitIsAvailable ? new Date() : null
        },
        query: getQueryUnits()
    }

    const response = await fetch(`${urlAPI}`, {
        method: 'POST',
        body: JSON.stringify(bodyObject),
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
        }
    })

    const responseJSON = await response.json() as unknown as any
    if (!unitIsAvailable && !currentUnit[0].attributes.Statuses.includes('Drafted') && !currentUnit[0].attributes.Statuses.includes('Sold')) {
        void sendUnitNotification(responseJSON.response.data as Unit, 'user-unit-new')
    }
    const { isSuccessful } = dispatch(singleUnitFetchingSuccess(responseJSON))
    if (!isSuccessful) {
        await dispatch(fetchUnits(true))
        await dispatch(fetchUser(true))
    }
    dispatch(dataFetchingFinish())
    return responseJSON
}

export const fetchSingleUnit = (unitID: number): any => {
    const jwt = window.localStorage.getItem(ACCESS_KEY)
    return async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
        try {
            const bodyObject = {
                method: 'GET',
                collection: 'units',
                id: unitID,
                query: getQueryUnits()
            }

            const response = await fetch(`${urlAPI}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${String(jwt)}`
                },
                body: JSON.stringify(bodyObject)
            })

            const responseJSON = await response.json()

            if (responseJSON.success === true) {
                if (responseJSON.response.error != null) {  
                    return {
                        isSuccessful: false
                    }
                } else {
                    return  { isSuccessful: true , unit : responseJSON.response?.data }
                }
            } else {
                return {
                    isSuccessful: false
                }
            }
        } catch (error) {
            console.log(error)
        }
    }
}

export const setUnitSold = (unitID: number, setInProcess: boolean = false , userID?:number | undefined, activity?: Omit<OffMarketActivityEvent, 'type' | 'createdAt'>)  => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    const unit = _.cloneDeep(getState().units.units?.find((unit) => unit.id === unitID))
    if (unit == null) {
        console.error(`Unit with id ${unitID} doesn't exist in application state`)
        return {
            isSuccessful: false
        }
    }
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const Statuses = setInProcess ? [...unit.attributes.Statuses].filter(status => !['Sold'].includes(status)) : [...unit.attributes.Statuses, 'Sold']
    const bodyObject = {
        method: 'PUT',
        collection: 'units',
        id: unitID,
        body: {
            Statuses ,
            HideDate: !setInProcess ? new Date(new Date().getTime() + 1000 * 60 * 60 * 24) : null
        },
        query: getQueryUnits()
    }

    const response = await fetch(`${urlAPI}`, {
        method: 'POST',
        body: JSON.stringify(bodyObject),
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
        }
    })

    const responseJSON = await response.json() as unknown as any
    responseJSON.isSuccessful = responseJSON.success


    // const userBroadcastMessages = getState().chat.userBroadcastMessages
    // if(userBroadcastMessages.length > 0){
    //     const currentBroadcast = userBroadcastMessages.find((_broadcast) => _broadcast?.Unit?.attributes?.id === Number(unitID) )
    //     if(currentBroadcast != null){
    //         const responseDeleteMessage = await fetch(`${urlAPI}`, {
    //             method: 'POST',
    //             body: JSON.stringify({
    //                 method: 'DELETE',
    //                 collection: 'messages',
    //                 id : currentBroadcast.id
    //             }),
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 Authorization: `Bearer ${jwt}`
    //             }
    //         })
    //         await responseDeleteMessage.json()
    //     }
    //     await dispatch(getChatList())
    // }


    const { isSuccessful } = dispatch(singleUnitFetchingSuccess(responseJSON))
    if (!isSuccessful) {
        await dispatch(fetchUnits(true))
        await dispatch(fetchUser(true))
    }
    if (setInProcess) {
        if (!unit.attributes.Statuses.includes('Drafted') && !unit.attributes.Statuses.includes('Archived')) {
            void sendUnitNotification(responseJSON.response.data as Unit, 'user-unit-new')
        }
        return responseJSON
    }

    if (responseJSON.isSuccessful === true && activity != null) {
        await dispatch(appendUnitActivity(unitID, {
            type: 'off_market',
            ...activity,
            createdAt: new Date().toISOString()
        }))
    }

    await sendUnitNotification(unit, 'user-unit-sold', userID ?
        { Text: "Offer is done!", 
            RuleData: { userID }
        } : {})

    return responseJSON
}

export const setUnitHidden = (unitID: number, setShown: boolean = false) => async (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    const unit = _.cloneDeep(getState().units.units?.find((unit) => unit.id === unitID))
    if (unit == null) {
        console.error(`Unit with id ${unitID} doesn't exist in application state`)
        return {
            isSuccessful: false
        }
    }
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const statuses = unit.attributes.Statuses
    const bodyObject = {
        method: 'PUT',
        collection: 'units',
        id: unitID,
        CreateDate: setShown ? new Date() : null,
        body: {
            Statuses: !setShown ? [...statuses, 'Drafted'] : statuses.filter(status => status !== 'Drafted')
        },
        query: getQueryUnits()
    }

    const response = await fetch(`${urlAPI}`, {
        method: 'POST',
        body: JSON.stringify(bodyObject),
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
        }
    })

    const responseJSON = await response.json() as unknown as any
    responseJSON.isSuccessful = responseJSON.success
    const { isSuccessful } = dispatch(singleUnitFetchingSuccess(responseJSON))
    if (!isSuccessful) {
        await dispatch(fetchUnits(true))
        await dispatch(fetchUser(true))
    }

    if (setShown && !unit.attributes.Statuses.includes('Archived') && !unit.attributes.Statuses.includes('Sold')) {
        void sendUnitNotification(responseJSON.response.data as Unit, 'user-unit-new')
    }

    return responseJSON
}





export const normalizeNumberField = (v: any) => {
  if (v === '' || v == null) return null;           // или undefined (см. ниже)
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
